import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface ComboboxOption {
	value: string;
	label: string;
}

/** Distinct names, sorted, each its own value and label. */
export const toOptions = (names: string[]): ComboboxOption[] =>
	[...new Set(names)].sort().map((name) => ({ value: name, label: name }));

/** A single-select list in a popover that can be narrowed by typing ("" = all). */
export function SearchCombobox({
	options,
	value,
	onChange,
	allLabel,
	searchPlaceholder,
	emptyText,
	ariaLabel,
}: {
	options: ComboboxOption[];
	value: string;
	onChange: (value: string) => void;
	allLabel: string;
	searchPlaceholder: string;
	emptyText: string;
	ariaLabel: string;
}) {
	const [open, setOpen] = useState(false);

	function pick(next: string) {
		onChange(next);
		setOpen(false);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					aria-label={ariaLabel}
					className={cn(
						"w-full justify-between bg-input px-3 font-normal shadow-inner",
						!value && "text-muted-foreground",
					)}
				>
					<span className="truncate">
						{options.find((o) => o.value === value)?.label || allLabel}
					</span>
					<ChevronsUpDown className="opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-[var(--radix-popover-trigger-width)] min-w-56 p-0"
			>
				<Command>
					<CommandInput placeholder={searchPlaceholder} />
					<CommandList>
						<CommandEmpty>{emptyText}</CommandEmpty>
						<CommandGroup>
							<CommandItem
								value={`__all__ ${allLabel}`}
								onSelect={() => pick("")}
							>
								{allLabel}
								<Check
									className={cn("ml-auto", value ? "opacity-0" : "opacity-100")}
								/>
							</CommandItem>
							{options.map((option) => (
								<CommandItem
									key={option.value}
									// cmdk filters on this text, so it carries the label, and the
									// value keeps two same-named entries apart
									value={`${option.label} ${option.value}`}
									onSelect={() => pick(option.value)}
								>
									{option.label}
									<Check
										className={cn(
											"ml-auto",
											option.value === value ? "opacity-100" : "opacity-0",
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
